// src/lib/resources/glossary/category-ui.tsx
//
// Single source of truth for per-glossary-category UI theming (icon + the
// several Tailwind color forms the glossary surfaces render).
//
// Previously five components each carried their own category→style map:
//   • GlossaryHero / GlossaryGrid    — `categoryIcons` (icon component)
//   • GlossaryGrid                   — `categoryColors` (subtle gradient overlay)
//   • GlossaryTermGrid               — `categoryColors` (bg/text/chip/hover/border token set)
//   • GlossaryNavigation             — per-category `color` family name
//   • FeaturedGlossary               — `categoryIcons` + `categoryColors` (bold gradient)
// and they had DRIFTED (e.g. FeaturedGlossary mapped `general` to a bar-chart
// icon while everyone else used the bookmark icon; `charts` has a different hue
// on every surface). This module co-locates each surface's value so the theming
// lives in one place.
//
// IMPORTANT: each field below preserves the EXACT class string that surface
// currently renders — folding these maps here is behaviour-preserving, not a
// re-theme. The one deliberate change is unifying `general`'s icon to the
// bookmark (the majority; FeaturedGlossary was the lone outlier). Genuinely
// unifying the divergent *hues* (e.g. picking one canonical `charts` color) is
// an owner aesthetic decision left as a follow-up — this module makes that drift
// visible in one spot rather than resolving it.
//
// Client-safe: depends only on the shared enums + icon components (no server or
// heavy deps), so any client component can import it.

import type { ComponentType } from 'react'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'
import RiBarChartBoxLine from '~/icons/remix/generated/RiBarChartBoxLine'
import RiPaletteLine from '~/icons/remix/generated/RiPaletteLine'
import RiRocketLine from '~/icons/remix/generated/RiRocketLine'
import RiBookmarkLine from '~/icons/remix/generated/RiBookmarkLine'

/** The bg/text/chip/hover/border token set rendered by `GlossaryTermGrid`. */
export interface GlossaryCategoryTokens {
  bg: string
  text: string
  chip: string
  hover: string
  border: string
}

/** All UI theming for one glossary category. */
export interface GlossaryCategoryTheme {
  /** Icon component (Hero / Grid / Featured). */
  icon: ComponentType<{ className?: string }>
  /** Tailwind color-family name keyed by the `GlossaryNavigation` pills. */
  colorName: 'indigo' | 'purple' | 'blue' | 'green' | 'gray'
  /** Subtle gradient overlay used by `GlossaryGrid` category headers. */
  subtleGradient: string
  /** Bold gradient used by `FeaturedGlossary` tiles. */
  boldGradient: string
  /** Card token set used by `GlossaryTermGrid`. */
  tokens: GlossaryCategoryTokens
}

/**
 * Per-category theming. Values are copied verbatim from the previous per-component
 * maps (see file header) so every surface renders identically, with the single
 * exception of `general.icon` (unified to the bookmark).
 */
export const GLOSSARY_CATEGORY_THEME: Record<GlossaryCategory, GlossaryCategoryTheme> = {
  metrics: {
    icon: RiBarChartBoxLine,
    colorName: 'indigo',
    subtleGradient: 'from-blue-500/10 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/20',
    boldGradient: 'from-blue-500 to-indigo-500',
    tokens: {
      bg: 'bg-indigo-50 dark:bg-indigo-900/20',
      text: 'text-indigo-600 dark:text-indigo-400',
      chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      hover: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
      border: 'group-hover:border-indigo-200 dark:group-hover:border-indigo-800/50',
    },
  },
  creative: {
    icon: RiPaletteLine,
    colorName: 'purple',
    subtleGradient: 'from-purple-500/10 to-pink-500/10 dark:from-purple-500/20 dark:to-pink-500/20',
    boldGradient: 'from-purple-500 to-pink-500',
    tokens: {
      bg: 'bg-pink-50 dark:bg-pink-900/20',
      text: 'text-pink-600 dark:text-pink-400',
      chip: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
      hover: 'group-hover:text-pink-600 dark:group-hover:text-pink-400',
      border: 'group-hover:border-pink-200 dark:group-hover:border-pink-800/50',
    },
  },
  platform: {
    icon: RiRocketLine,
    colorName: 'blue',
    subtleGradient: 'from-indigo-500/10 to-purple-500/10 dark:from-indigo-500/20 dark:to-purple-500/20',
    boldGradient: 'from-indigo-500 to-purple-500',
    tokens: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      text: 'text-blue-600 dark:text-blue-400',
      chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      hover: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
      border: 'group-hover:border-blue-200 dark:group-hover:border-blue-800/50',
    },
  },
  general: {
    // Unified to the bookmark icon — Hero/Grid used this already; FeaturedGlossary
    // was the lone outlier (bar-chart).
    icon: RiBookmarkLine,
    colorName: 'gray',
    subtleGradient: 'from-emerald-500/10 to-teal-500/10 dark:from-emerald-500/20 dark:to-teal-500/20',
    boldGradient: 'from-gray-500 to-gray-600',
    tokens: {
      bg: 'bg-gray-50 dark:bg-gray-900/20',
      text: 'text-gray-600 dark:text-gray-400',
      chip: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      hover: 'group-hover:text-gray-600 dark:group-hover:text-gray-400',
      border: 'group-hover:border-gray-200 dark:group-hover:border-gray-800/50',
    },
  },
  charts: {
    icon: RiBarChartBoxLine,
    colorName: 'green',
    subtleGradient: 'from-blue-500/10 to-cyan-500/10 dark:from-blue-500/20 dark:to-cyan-500/20',
    boldGradient: 'from-blue-500 to-cyan-500',
    tokens: {
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      text: 'text-purple-600 dark:text-purple-400',
      chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      hover: 'group-hover:text-purple-600 dark:group-hover:text-purple-400',
      border: 'group-hover:border-purple-200 dark:group-hover:border-purple-800/50',
    },
  },
}

/**
 * Returns the {@link GlossaryCategoryTheme} for a category, falling back to
 * `general` for an unknown/malformed slug (so a caller never dereferences
 * `undefined`).
 *
 * @param category - Glossary category slug.
 * @returns The category's theme.
 */
export function getGlossaryCategoryTheme(category: string): GlossaryCategoryTheme {
  return (
    GLOSSARY_CATEGORY_THEME[category as GlossaryCategory] ?? GLOSSARY_CATEGORY_THEME.general
  )
}
